import { useState, useCallback } from 'react';
import { getAllArticles, getArticle, createArticle, updateArticle, deleteArticle } from '../services/articleApi';

export function useArticle() {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [loadingArticleDetails, setLoadingArticleDetails] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [error, setError] = useState(null);

  const loading = loadingArticles || loadingArticleDetails || loadingAdd || loadingUpdate || loadingDelete;

  const fetchArticles = useCallback(async () => {
    setLoadingArticles(true);
    setError(null);
    try {
      const data = await getAllArticles();
      setArticles(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch articles');
    } finally {
      setLoadingArticles(false);
    }
  }, []);

  const fetchArticleById = useCallback(async (id) => {
    setLoadingArticleDetails(true);
    setError(null);
    try {
      const data = await getArticle(id);
      setSelectedArticle(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch article');
      return null;
    } finally {
      setLoadingArticleDetails(false);
    }
  }, []);

  const addArticle = useCallback(async (article, idToken) => {
    setLoadingAdd(true);
    setError(null);
    try {
      const data = await createArticle(article, idToken);
      setArticles((prev) => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to create article');
      return null;
    } finally {
      setLoadingAdd(false);
    }
  }, []);

  const updateArticleHook = useCallback(async (id, article, idToken) => {
    setLoadingUpdate(true);
    setError(null);
    try {
      const updated = await updateArticle(id, article, idToken);
      const updatedArticle = updated && typeof updated === 'object' ? updated : { ...article, id };

      setArticles((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...updatedArticle, id } : item))
      );
      setSelectedArticle((prev) =>
        prev && prev.id === id ? { ...prev, ...updatedArticle, id } : prev
      );
      return true;
    } catch (err) {
      setError(err.message || 'Failed to update article');
      return false;
    } finally {
      setLoadingUpdate(false);
    }
  }, []);

  const deleteArticleHook = useCallback(async (id, idToken) => {
    setLoadingDelete(true);
    setError(null);
    try {
      await deleteArticle(id, idToken);
      setArticles((prev) => prev.filter((article) => article.id !== id));
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete article');
      return false;
    } finally {
      setLoadingDelete(false);
    }
  }, []);

  return {
    articles,
    selectedArticle,
    loading,
    loadingArticles,
    loadingArticleDetails,
    loadingAdd,
    loadingUpdate,
    loadingDelete,
    error,
    fetchArticles,
    fetchArticleById,
    addArticle,
    updateArticle: updateArticleHook,
    deleteArticle: deleteArticleHook,
    setSelectedArticle,
  };
}
